import { useRef, useLayoutEffect, useState } from "react"
import { ChevronDown } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"
import { cn } from "@/lib/utils"

export interface StrategyNavTab {
  id: string
  label: string
}

export interface StrategyNavTabsProps {
  tabs: StrategyNavTab[]
  activeId: string
  onTabChange: (id: string) => void
  isMobile?: boolean
  className?: string
}

export function StrategyNavTabs({
  tabs,
  activeId,
  onTabChange,
  isMobile = false,
  className,
}: StrategyNavTabsProps) {
  if (isMobile) {
    return (
      <MobileDropdown
        tabs={tabs}
        activeId={activeId}
        onTabChange={onTabChange}
        className={className}
      />
    )
  }
  return (
    <DesktopTabs tabs={tabs} activeId={activeId} onTabChange={onTabChange} className={className} />
  )
}

function DesktopTabs({
  tabs,
  activeId,
  onTabChange,
  className,
}: StrategyNavTabsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [indicator, setIndicator] = useState({ left: 0, width: 0 })
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const btn = container.querySelector<HTMLButtonElement>(`[data-tab="${activeId}"]`)
    if (!btn) return
    setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth })
  }, [activeId])

  return (
    <div ref={containerRef} className={cn("relative flex items-center gap-1", className)}>
      {tabs.map((t) => {
        const isActive = activeId === t.id
        return (
          <button
            key={t.id}
            data-tab={t.id}
            onClick={() => onTabChange(t.id)}
            className={cn(
              "relative px-3 h-11 cursor-pointer",
              "transition-[color,font-weight,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]",
              "active:scale-[0.97]",
              isActive
                ? "text-body-strong text-foreground"
                : "text-nav text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        )
      })}
      <span
        aria-hidden="true"
        className="absolute bottom-0 h-[2px] bg-foreground transition-[left,width] duration-[250ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
        style={{ left: `${indicator.left}px`, width: `${indicator.width}px` }}
      />
    </div>
  )
}

function MobileDropdown({ tabs, activeId, onTabChange, className }: StrategyNavTabsProps) {
  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0]
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 h-11 px-2 cursor-pointer select-none text-body-strong text-foreground transition-colors hover:text-foreground",
            className,
          )}
        >
          <span className="truncate max-w-[180px]">{activeTab?.label ?? ""}</span>
          <ChevronDown
            size={14}
            strokeWidth={1.75}
            className="text-muted-foreground transition-transform duration-200 data-[state=open]:rotate-180 shrink-0"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4} className="v1 w-56">
        {tabs.map((t) => {
          const isActive = activeId === t.id
          return (
            <DropdownMenuItem
              key={t.id}
              onSelect={() => onTabChange(t.id)}
              className={cn("text-body", isActive && "text-foreground font-medium")}
            >
              {t.label}
              {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
